import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {switchMap, map, catchError} from 'rxjs/operators';
import { LoginAction } from './actions';
import {forkJoin, of} from 'rxjs';
import { PvUserSession } from "@models/user-session";
import { JwtHelperService } from "@auth0/angular-jwt";
import { UserSessionService } from "@services/user-session/user-session.service";
import { Numbers } from "@constants/numbers";
import { RolUserService } from "@services/rol-user/rol-user.service";
import { RolNameService } from "@services/rol-name/rol-name.service";
import { PvRolUser } from "@models/rol-user";
import { PvRolName } from "@models/rol-name";
import {MsalJwtToken, MsalSessionInfo} from "@models/jwt-token";
import * as moment from 'moment';
import {AuthenticationService} from "@services/authentication/authentication.service";
import {HttpErrorResponse} from "@angular/common/http";

@Injectable()
export class LoginEffects {

  jwtHelperService: JwtHelperService;

  constructor(
    private actions$: Actions,
    private userSessionService: UserSessionService,
    private rolUserService: RolUserService,
    private rolNameService: RolNameService,
    private authenticationService: AuthenticationService,
  ) {
    this.jwtHelperService = new JwtHelperService();
  }

  /** Init session **/
  initSessionEffectErrorMapper = (error: HttpErrorResponse) =>
    of(LoginAction.onError(error.error));

  getTokenInfoEffect = (): MsalSessionInfo => {
    const jwtToken = this.authenticationService.getToken();
    const tokenInfo = this.jwtHelperService.decodeToken(jwtToken) ?? {} as MsalJwtToken;
    return { jwtToken, tokenInfo };
  }

  saveUserSessionEffect = (msalSessionInfo: MsalSessionInfo) => {
    const { tokenInfo } = msalSessionInfo;
    const userSession: PvUserSession = {
      userEmail: tokenInfo.preferred_username,
      jwtToken: msalSessionInfo.jwtToken,
      createdAt: this.formatMilliseconds(tokenInfo.iat),
      expiredAt: this.formatMilliseconds(tokenInfo.exp),
      isActive: true
    };
    return this.userSessionService.save(userSession)
      .pipe(
        switchMap(this.getRolsEffectSwitchMapFuntion),
        catchError(this.initSessionEffectErrorMapper)
      );
  }

  formatMilliseconds(millisegs: number): string {
    millisegs = millisegs ?? 0;
    const date = new Date(millisegs * Numbers._1000);
    return moment(date).format('YYYY-MM-DDTHH:mm:ss');
  }

  getRolsEffectSwitchMapFuntion = (activeUserSession: PvUserSession)=> {
    return forkJoin({
      userRols: this.rolUserService.getRolesByUser(activeUserSession.userEmail),
      systemRols: this.rolNameService.getAll()
    })
      .pipe(
        map(this.getUserRolsEffectMapFuntion),
        switchMap(this.initSessionEffectMapper)
      );
  }

  getUserRolsEffectMapFuntion = (data:{ userRols: PvRolUser[], systemRols: PvRolName[] }): PvRolName | null => {
    if (data.userRols.length === Numbers._0) {
      return null;
    }
    return data.systemRols.find(rol => rol.rolId === data.userRols[Numbers._0].rolId) ?? null;
  }

  initSessionEffectMapper = (rol: PvRolName | null) => {
    const rolNewState = { rolId: rol?.rolId ?? null, rolName: rol?.rolName ?? null }
    return of(LoginAction.setRol(rolNewState));
  }

  initSessionEffectFunction = () =>
    this.actions$.pipe(
      ofType(LoginAction.initSesion),
      map(this.getTokenInfoEffect),
      switchMap(this.saveUserSessionEffect),
    );

  initSessionEffect$ = createEffect(this.initSessionEffectFunction);

}
